<script>
import { store } from '../../functions/mixins';
import * as sdk from '../../functions/sdk';
import UiHeadline from '../ui/Headline';
import UiSubheader from '../ui/Subheader';
import UiStepper from '../ui/Stepper';
import UiLoading from '../ui/Loading';
import UiScrollbar from '../ui/Scrollbar';
import deepEqual from 'deep-equal';

export default {
    mixins: [store],
    props: {
        value: Object,
        disabled: Boolean,
        skipStep: Function,
    },
    components: {
        UiHeadline,
        UiSubheader,
        UiLoading,
        UiScrollbar,
    },
    data: () => ({
        data: null,
        banners: [],
        bannersLoading: false,
    }),
    computed: {
        bannersSorted() {
            return this.banners === null ? [] : this.banners.sort((a, b) => b.id - a.id);
        },
    },
    created() {
        this.data = Object.assign({}, this.value);
        if (!this.disabled) {
            this.fetchBanners();
        }
    },
    methods: {
        fetchBanners() {
            this.bannersLoading = true;
            sdk.pressengerControl.listBrandPics().then(banners => {
                this.banners = banners;
                this.bannersLoading = false;
                if (!this.banners.length) {
                    this.skipStep();
                }
            }).catch(() => {
                setTimeout(() => {
                    this.fetchBanners();
                }, 1000);
            });
        },
        selectBanner(banner) {
            if (this.data.banner === banner) {
                this.data.banner = null;
            } else {
                this.data.banner = banner;
            }
        },
        onPrevClicked() {
            if (this.bannersLoading || !this.bannersSorted.length) {
                return;
            }
            if (!this.data.banner) {
                this.data.banner = this.bannersSorted[0];
                return;
            }
            let index = 0;
            this.bannersSorted.forEach((t, i) => {
                if (t === this.data.banner) {
                    index = i;
                }
            });
            index--;
            if (index < 0) {
                index = this.bannersSorted.length - 1;
            }
            this.data.banner = this.bannersSorted[index];
        },
        onNextClicked() {
            if (this.bannersLoading || !this.bannersSorted.length) {
                return;
            }
            if (!this.data.banner) {
                this.data.banner = this.bannersSorted[0];
                return;
            }
            let index = 0;
            this.bannersSorted.forEach((t, i) => {
                if (t === this.data.banner) {
                    index = i;
                }
            });
            index++;
            if (index >= this.bannersSorted.length) {
                index = 0;
            }
            this.data.banner = this.bannersSorted[index];
        },
    },
    watch: {
        value: {
            deep: true,
            handler(nextVal) {
                if (!deepEqual(nextVal, this.data)) {
                    this.data = Object.assign({}, nextVal);
                }
            }
        },
        data: {
            deep: true,
            handler: function(nextVal) {
                if (!deepEqual(nextVal, this.value)) {
                    this.$emit('input', Object.assign({}, nextVal));
                }
            }
        },
    },
}
</script>

<template>
    <div class="StepBanners">
        <!--
        <ui-subheader>{{ $t('stepBanners.step') }} 5<span>/5</span> &bull;</ui-subheader>
        <ui-headline>{{ $t('stepBanners.title') }}</ui-headline>
        -->
        <ui-loading v-if="bannersLoading" />
        <ui-scrollbar v-else :height="420">
            <div class="Grid AltImageSize">
                <div
                    v-for="(i, index) in bannersSorted"
                    :class="{
                        GridItem: true,
                        Active: i === data.banner,
                        NoBorderLeft: index % 2 !== 0,
                        NoBorderTop: index > 1,
                    }"
                    :key="i.id"
                    @click="selectBanner(i)"
                >
                    <div class="Toolbar">
                        <span>{{ i.link }}</span>
                    </div>
                    <div class="ImageWrapper">
                        <img v-if="i.url" :src="i.url" alt="" />
                        <iframe v-else :src="i.link" scrolling="no" sandbox="allow-scripts allow-same-origin" />
                    </div>
                    <div class="Check">
                        <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid" width="16.5" height="13.5" viewBox="0 0 16.5 13.5" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M16.038 2.741c.298-.305.462-.708.462-1.133 0-.43-.164-.833-.462-1.138a1.55 1.55 0 00-2.222 0L4.869 9.616 2.684 7.385a1.548 1.548 0 00-2.222 0A1.614 1.614 0 000 8.522c0 .429.164.832.462 1.133l3.293 3.365a1.55 1.55 0 002.222 0"/></svg>
                    </div>
                </div>
            </div>
        </ui-scrollbar>
    </div>
</template>

<style lang="stylus" scoped>
@import '../../stylus/vars.styl'

.Grid
    display flex
    justify-content flex-start
    align-items flex-start
    align-content flex-start
    flex-wrap wrap
    min-height 100%
    box-sizing border-box
    max-width 700px
    .GridItem
        display flex
        flex-direction column
        justify-content center
        align-items center
        width 25%
        position relative
        border 1px solid $cBorder
        box-sizing border-box
        padding 20px
        transition .3s all
        background rgba(255,255,255,.4)
        cursor: pointer;
        &.NoBorderTop
            border-top none
        &.NoBorderLeft
            border-left none
        .Toolbar
            font-size 12px
            font-weight 600
            color $cPrimary
            align-self flex-start
            overflow hidden
            white-space nowrap
            max-width 100%
        .ImageWrapper
            overflow hidden
            height 110px
            display flex
            justify-content center
            align-items center
            img
                max-width 110px
                max-height 110px
            iframe
                border none
                width 880px
                height 880px
                transform scale(1/8)
        .Check
            position absolute
            opacity 0
            top 0
            left 0
            right 0
            bottom 0
            background rgba(255,255,255,.7)
            display flex
            align-items center
            justify-content center
            transition .3s all
            svg
                transform scale(2)
            path
                fill $cAccent
        &.Active
            background #fff
            transform scale(1.08)
            z-index 2
            border-color white
            box-shadow 0 40px 100px rgba(0,0,0,.2)
            .Check
                opacity 1
            img
                filter blur(3px)
        &:hover
            background #fff
    &.AltImageSize
        .GridItem
            width 50%
            padding 5px
            .ImageWrapper
                height 110px
                width 100%
            .ImageWrapper
                img
                    max-height 80px
                    max-width 100%
                iframe
                    height 640px
                    width 880px
                    transform scale(1/8)

.StepBanners .Scrollbar
    padding 40px 70px 150px 70px !important
    margin -40px -50px -150px -70px
    .ps__scrollbar-y-rail
        right 50px !important
        top 50px !important
</style>
